import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * iter137 — useTutorialStatus
 *
 * Read/write "already seen" flags for feature-onboarding tours.
 * Persists to the backend (`users.tutorials.<key>`) so it works
 * cross-device; falls back to localStorage when the network is
 * unavailable (cold start, airplane mode).
 *
 * Usage:
 *   const { seen, loading, markSeen } = useTutorialStatus('schedina_analyzer');
 */
const _lsKey = (key) => `tutorial_seen_${key}`;

export default function useTutorialStatus(key) {
  // Initialize from localStorage synchronously so the modal doesn't
  // flash on a fast device before the /api/user/tutorials call resolves.
  const [seen, setSeen] = useState(() => {
    try { return localStorage.getItem(_lsKey(key)) === '1'; }
    catch { return false; }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/user/tutorials`);
        if (cancelled) return;
        const remote = !!res.data?.[key];
        setSeen(remote);
        if (remote) {
          try { localStorage.setItem(_lsKey(key), '1'); } catch {}
        }
      } catch {
        // Offline / 401 → keep localStorage value
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [key]);

  const markSeen = useCallback(async () => {
    setSeen(true);
    try { localStorage.setItem(_lsKey(key), '1'); } catch {}
    try {
      await axios.post(`${API}/user/tutorials/${key}/seen`);
    } catch {
      // Server-side write can be retried on next hook mount; localStorage
      // is authoritative locally for the current session.
    }
  }, [key]);

  return { seen, loading, markSeen };
}
